import React from 'react';

import { Box, Flex } from '@chakra-ui/react';
import useOrganisationSettings from 'components/hooks/useOrganisationSettings';
import { TruncatedTextBodyMedium } from 'screens/Levelup/LevelUpCF/constants';

import { KeyResultPrivate2, PrivateObjective } from 'assets/assetsV1/iconComponents';
import { KeyResult } from 'assets/assetsV1/iconComponents/KeyResult';
import { Objective } from 'assets/assetsV1/iconComponents/Objective';

import { OKR_PROGRESS_TYPES } from 'constants/ProjectConstants';
import { StringModifier, modifyString } from 'constants/copyHelpers';

import { KRNameAndProgressCell } from './KRNameAndProgressCell';
import { ObjectiveNameAndProgressCell } from './ObjectiveNameAndProgressCell';

export const ObjectiveKRCell = ({ data }) => {
  const { type, visibilityType, unMasked } = data ?? {};
  const isObjective = type === 'OBJECTIVE';

  const isPrivateOKR = visibilityType === 'PRIVATE';

  const { moduleName, objectivesName, keyResultsName } = useOrganisationSettings();

  if (!unMasked) {
    return (
      <Box w="100%">
        <Flex alignItems="flex-start">
          <KeyResultPrivate2 color="#4657AE" boxSize="24px" />
          <Flex ml="6px" flexDir="column" w="100%">
            <TruncatedTextBodyMedium
              style={{ fontStyle: 'italic' }}
              className="highlight-able-text"
              mb={isObjective ? '1px' : 0}
            >
              You do not have access to this private {modifyString(moduleName, StringModifier.SENTENCE_CASE)}
            </TruncatedTextBodyMedium>
          </Flex>
        </Flex>
      </Box>
    );
  }

  /* 
    find and report errors in okr
    1. if tracking type is dependent and okr has no children
    2. if tracking type is derived_sum and children are not summable.
  */

  const isDerivedSum = OKR_PROGRESS_TYPES.DERIVED_SUM === data?.progressType;
  const isDerivedAvg = OKR_PROGRESS_TYPES.DERIVED_AVG === data?.progressType;
  const okrHasNoChildren = data?.ifGoalChildExists === false;
  const areChildSummable = data?.areChildSummable !== false;

  const isTrackedAsDependentWithNoChildren = (isDerivedAvg || isDerivedSum) && okrHasNoChildren;

  const isTrackedAsDerivedSumWithNonSummableChildren = isDerivedSum && !areChildSummable;

  const hasError = isTrackedAsDependentWithNoChildren || isTrackedAsDerivedSumWithNonSummableChildren;

  let errorMessage = '';

  if (hasError && isTrackedAsDependentWithNoChildren) {
    errorMessage = `${moduleName} is tracked as 'Dependent' but has no child ${modifyString(
      moduleName,
      StringModifier.PLURAL_SENTENCE_CASE
    )}`;
  }

  if (hasError && isTrackedAsDerivedSumWithNonSummableChildren) {
    errorMessage = `${moduleName} is tracked as 'Dependent - Sum' but has child ${modifyString(
      moduleName,
      StringModifier.PLURAL_SENTENCE_CASE
    )} whose metric/measures are not summable`;
  }

  return (
    <Box pt={2} pb={3} px={1}>
      <Flex alignItems="flex-start" width="100%">
        {isObjective ? (
          <>
            {isPrivateOKR ? (
              <PrivateObjective color="#7651BC" boxSize="24px" />
            ) : (
              <Objective color="#7651BC" boxSize="24px" />
            )}

            <ObjectiveNameAndProgressCell
              hasError={hasError}
              errorMessage={errorMessage}
              data={data}
              containerProps={{ ml: '6px', width: 'calc(100% - 30px)' }}
            />
          </>
        ) : (
          <>
            {isPrivateOKR ? (
              <KeyResultPrivate2 color="#4657AE" boxSize="24px" />
            ) : (
              <KeyResult color="#4657AE" boxSize="24px" />
            )}
            <KRNameAndProgressCell
              hasError={hasError}
              errorMessage={errorMessage}
              data={data}
              containerProps={{ ml: '6px', width: 'calc(100% - 30px)' }}
            />
          </>
        )}
      </Flex>
    </Box>
  );
};
